import type { ListBuilderConfiguration } from '../../types';
import { quoteShopAtRiskList } from './lists/quote-shop-at-risk';
import { quoteShopDefaultList } from './lists/quote-shop-default';
import { quoteShopPreviouslyPurchasedList } from './lists/quote-shop-previously-purchased';
import { quoteShopShortDateList } from './lists/quote-shop-short-date';
import { quoteShopSuggestedList } from './lists/quote-shop-suggested';

/**
 * Quote Shop list configuration
 * Shop items for quote entry with various filter lists
 */
export const QUOTE_SHOP_CONFIG: ListBuilderConfiguration = {
	lists: [
		quoteShopDefaultList,
		quoteShopSuggestedList,
		quoteShopAtRiskList,
		quoteShopPreviouslyPurchasedList,
		quoteShopShortDateList,
	],
	paging: {
		defaultPageSize: 20,
		pageSizeOptions: [10, 20, 50, 100],
	},
	showResetButton: true,
	filters: {
		activeFilters: [
			'item_search',
			'item_filters',
			'category',
			'subcategory',
			'manufacturer',
		],
		defaultFilters: {},
		availableFilters: [
			'item_search',
			'item_filters',
			'category',
			'subcategory',
			'manufacturer',
		],
	},
	objects: [
		{
			baseUrl: '/items/:id',
			objectLabel: 'Shop Item',
			objectTable: 'item',
			selectedFields: [
				{ fieldName: 'soonestExpirationDate', fieldLabel: 'Next Exp' },
				{ fieldName: '_id', fieldLabel: 'ID' },
				{ fieldName: 'subcategory', fieldLabel: 'Subcat' },
				{ fieldName: 'description', fieldLabel: 'Desc' },
				{ fieldName: 'manufacturer', fieldLabel: 'Mfg' },
				{ fieldName: 'revenueLastMonth', fieldLabel: 'Rev Last Month' },
				{ fieldName: 'revenueTwoMonthsAgo', fieldLabel: 'Rev 2 Mo Ago' },
				{ fieldName: 'revenueMtd', fieldLabel: 'Rev MTD' },
				{ fieldName: 'quantityOnOrder', fieldLabel: 'Qty On Order' },
				{ fieldName: 'sizeCount', fieldLabel: 'Size/Count' },
				{ fieldName: 'minimumPrice', fieldLabel: 'Minimum Price' },
				{ fieldName: 'externalId', fieldLabel: 'External Id' },
				{ fieldName: 'externalId2', fieldLabel: 'External Id 2' },
				{ fieldName: 'strength', fieldLabel: 'Strength' },
				{ fieldName: 'unitOfMeasure', fieldLabel: 'Unit of Measure' },
				{ fieldName: 'price', fieldLabel: 'Price' },
				{ fieldName: 'category', fieldLabel: 'Cat' },
				{ fieldName: 'quantityOnHand', fieldLabel: 'Qty On-Hand' },
				{ fieldName: 'ndc', fieldLabel: 'NDC' },
				{ fieldName: 'acquisitionCost', fieldLabel: 'Acquisition Cost' },
				{ fieldName: 'sellCost', fieldLabel: 'Sell Cost' },
				{ fieldName: 'averageCost', fieldLabel: 'Average Cost' },
				{ fieldName: 'averageNetCost', fieldLabel: 'Average Net Cost' },
				// Pricing & Costs
				{ fieldName: 'previousPrice', fieldLabel: 'Previous Price' },
				{
					fieldName: 'averageWholesalePrice',
					fieldLabel: 'Avg Wholesale Price',
				},
				{ fieldName: 'conversionFactor', fieldLabel: 'Conversion Factor' },
				// Inventory & Fulfillment
				{ fieldName: 'caseSize', fieldLabel: 'Case Size' },
				{ fieldName: 'quantityOnBackorder', fieldLabel: 'Qty On Backorder' },
				{ fieldName: 'dailyLimit', fieldLabel: 'Daily Limit' },
				{ fieldName: 'monthlyLimit', fieldLabel: 'Monthly Limit' },
				{ fieldName: 'perOrderLimit', fieldLabel: 'Per Order Limit' },
				// Product Details
				{ fieldName: 'extendedDescription', fieldLabel: 'Extended Desc' },
				{
					fieldName: 'manufacturerItemId',
					fieldLabel: 'Manufacturer Item ID',
				},
				{ fieldName: 'brandEquivalent', fieldLabel: 'Brand Equivalent' },
				{ fieldName: 'countryOfOrigin', fieldLabel: 'Country of Origin' },
				{ fieldName: 'family', fieldLabel: 'Family' },
				{ fieldName: 'storageType', fieldLabel: 'Storage Type' },
				{ fieldName: 'tradeStyle', fieldLabel: 'Trade Style' },
				{ fieldName: 'weight', fieldLabel: 'Weight' },
				{ fieldName: 'length', fieldLabel: 'Length' },
				{ fieldName: 'width', fieldLabel: 'Width' },
				{ fieldName: 'height', fieldLabel: 'Height' },
				// Identifiers / UPCs
				{ fieldName: 'standardNdc', fieldLabel: 'Standard NDC' },
				{ fieldName: 'upc', fieldLabel: 'UPC' },
				{ fieldName: 'caseUpc', fieldLabel: 'Case UPC' },
				{ fieldName: 'retailUpc', fieldLabel: 'Retail UPC' },
				{ fieldName: 'extraUpc', fieldLabel: 'Extra UPC' },
				// Flags
				{ fieldName: 'isActive', fieldLabel: 'Is Active' },
				{ fieldName: 'isShortDate', fieldLabel: 'Is Short Date' },
				{ fieldName: 'isHazmat', fieldLabel: 'Is Hazmat' },
				{ fieldName: 'isRefrigerated', fieldLabel: 'Is Refrigerated' },
				{ fieldName: 'isHeavy', fieldLabel: 'Is Heavy' },
				{
					fieldName: 'ecommerceVisibility',
					fieldLabel: 'Ecommerce Visibility',
				},
				// Dates
				{
					fieldName: 'soonestArrivalDate',
					fieldLabel: 'Soonest Arrival Date',
				},
				{ fieldName: 'lastOutOfStockDate', fieldLabel: 'Last Out of Stock' },
				{ fieldName: 'lastImportDate', fieldLabel: 'Last Import Date' },
				{
					fieldName: 'externalCreatedDate',
					fieldLabel: 'External Created Date',
				},
				{
					fieldName: 'priceModifiedDatetime',
					fieldLabel: 'Price Modified Date',
				},
				// Sales Aggregates
				{ fieldName: 'invoiceFrequency', fieldLabel: 'Invoice Frequency' },
				{
					fieldName: 'soldUnitsAggregate',
					fieldLabel: 'Sold Units (All)',
				},
				{ fieldName: 'revenueAggregate', fieldLabel: 'Revenue (All)' },
				{
					fieldName: 'uniqueClientsAggregate',
					fieldLabel: 'Unique Clients (All)',
				},
				{
					fieldName: 'uniqueInvoicesAggregate',
					fieldLabel: 'Unique Invoices (All)',
				},
				{ fieldName: 'soldUnitsMtd', fieldLabel: 'Sold Units MTD' },
				{
					fieldName: 'soldUnitsLastMonth',
					fieldLabel: 'Sold Units Last Mo',
				},
				{
					fieldName: 'soldUnitsTwoMonthsAgo',
					fieldLabel: 'Sold Units 2 Mo Ago',
				},
				{ fieldName: 'uniqueClientsMtd', fieldLabel: 'Unique Clients MTD' },
				{
					fieldName: 'uniqueClientsLastMonth',
					fieldLabel: 'Unique Clients Last Mo',
				},
				{
					fieldName: 'uniqueClientsTwoMonthsAgo',
					fieldLabel: 'Unique Clients 2 Mo Ago',
				},
				{
					fieldName: 'uniqueInvoicesMtd',
					fieldLabel: 'Unique Invoices MTD',
				},
				{
					fieldName: 'uniqueInvoicesLastMonth',
					fieldLabel: 'Unique Invoices Last Mo',
				},
				{
					fieldName: 'uniqueInvoicesTwoMonthsAgo',
					fieldLabel: 'Unique Invoices 2 Mo Ago',
				},
				// GP & Margin
				{ fieldName: 'totalGpAggregate', fieldLabel: 'Total GP (All)' },
				{ fieldName: 'totalGpMargin', fieldLabel: 'Total GP Margin' },
				{
					fieldName: 'rollingTotalGpAggregate30',
					fieldLabel: 'Rolling GP 30d',
				},
				{
					fieldName: 'rollingTotalGpAggregate90',
					fieldLabel: 'Rolling GP 90d',
				},
				{
					fieldName: 'rollingTotalGpMargin30',
					fieldLabel: 'Rolling GP Margin 30d',
				},
				{
					fieldName: 'rollingTotalGpMargin90',
					fieldLabel: 'Rolling GP Margin 90d',
				},
				{ fieldName: 'rollingRevenue30', fieldLabel: 'Rolling Revenue 30d' },
				{ fieldName: 'rollingRevenue90', fieldLabel: 'Rolling Revenue 90d' },
				{
					fieldName: 'rollingTotalGpAggregateTrend30',
					fieldLabel: 'GP Trend 30d',
				},
				{
					fieldName: 'rollingTotalGpAggregateTrend90',
					fieldLabel: 'GP Trend 90d',
				},
				{
					fieldName: 'rollingTotalGpMarginTrend30',
					fieldLabel: 'GP Margin Trend 30d',
				},
				{
					fieldName: 'rollingTotalGpMarginTrend90',
					fieldLabel: 'GP Margin Trend 90d',
				},
				{
					fieldName: 'rollingRevenueTrend30',
					fieldLabel: 'Revenue Trend 30d',
				},
				{
					fieldName: 'rollingRevenueTrend90',
					fieldLabel: 'Revenue Trend 90d',
				},
			],
		},
		{
			baseUrl: '/invoices/:id',
			objectLabel: 'Invoice',
			objectTable: 'invoice',
			selectedFields: [],
		},
		{
			// PD-15581: registers the purchase-order object so a Soonest Arrival
			// field (idField soonestArrivalPurchaseOrderId) resolves to
			// /purchase-orders/:id, matching the item list. Without the entry the
			// link resolver falls back to the FIRST object in this array — item —
			// and would build /items/<purchaseOrderId>, a link to the wrong record.
			baseUrl: '/purchase-orders/:id',
			objectLabel: 'Purchase Order',
			objectTable: 'purchase_order',
			selectedFields: [],
		},
	],
	tableSorts: [
		{
			id: 'in_stock_first',
			// The "(Yes First)" indicator is what keeps this id stable: the sort
			// dropdown auto-splits any table sort whose name lacks one into
			// "-asc"/"-desc" variants, and neither would match the saved id.
			name: 'In Stock (Yes First)',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'inStock',
					objectTable: 'item',
				},
				{
					direction: 'ASC',
					fieldName: 'description',
					objectTable: 'item',
				},
			],
			description:
				'Items in stock at a warehouse this client can buy from first, then by item name',
		},
		{
			id: 'name_asc',
			name: 'Name (A-Z)',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'description',
					objectTable: 'item',
				},
			],
			description: 'Sort by item name ascending',
		},
		{
			id: 'name_desc',
			name: 'Name (Z-A)',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'description',
					objectTable: 'item',
				},
			],
			description: 'Sort by item name descending',
		},
		{
			id: 'category_asc',
			name: 'Category (A-Z)',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'category',
					objectTable: 'item',
				},
			],
			description: 'Sort by category ascending',
		},
	],
};
